import React, { useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Search,
  FileText,
  Mail,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Settings,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { FAQ_DATA, FAQ_CATEGORIES, replaceFAQVariables } from '@/content/faqs';
import { fireEvent } from '@/lib/analytics';
import { useRevealOnce } from '@/hooks/useRevealOnce';

// Lightweight, optimized parallax engine scoped to this file
type Axis = 'y' | 'x' | 'both';
interface ParallaxOptions {
  speed?: number;
  axis?: Axis;
  maxTranslate?: number;
  rootMargin?: string;
}
interface ParallaxItem {
  element: HTMLElement;
  speed: number;
  axis: Axis;
  maxTranslate: number;
  inView: boolean;
  lastTransform: string;
}

const parallaxItems = new Set<ParallaxItem>();
let parallaxIo: IntersectionObserver | null = null;
let parallaxRafId: number | null = null;
let parallaxRunning = false;
let prefersReducedMotion =
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function ensureParallaxIO(rootMargin: string) {
  if (parallaxIo) return;
  parallaxIo = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const item = [...parallaxItems].find((it) => it.element === entry.target);
        if (!item) continue;
        item.inView = entry.isIntersecting;
      }
      requestParallaxTick();
    },
    { root: null, threshold: 0, rootMargin },
  );
}

function onParallaxScroll() {
  requestParallaxTick();
}

function onParallaxResize() {
  requestParallaxTick();
}

function startParallax() {
  if (parallaxRunning) return;
  parallaxRunning = true;
  window.addEventListener('scroll', onParallaxScroll, { passive: true });
  window.addEventListener('resize', onParallaxResize, { passive: true });
  requestParallaxTick();
}

function stopParallax() {
  if (!parallaxRunning) return;
  window.removeEventListener('scroll', onParallaxScroll);
  window.removeEventListener('resize', onParallaxResize);
  parallaxRunning = false;
}

function requestParallaxTick() {
  if (parallaxRafId !== null) return;
  parallaxRafId = window.requestAnimationFrame(updateParallax);
}

function clamp(val: number, min: number, max: number) {
  return Math.min(Math.max(val, min), max);
}

function updateParallax() {
  parallaxRafId = null;
  if (prefersReducedMotion) {
    parallaxItems.forEach((item) => {
      if (item.lastTransform !== '') {
        item.element.style.transform = '';
        item.lastTransform = '';
      }
    });
    return;
  }

  const viewportHeight = window.innerHeight || 0;
  const viewportWidth = window.innerWidth || 0;
  const centerY = viewportHeight / 2;
  const centerX = viewportWidth / 2;
  const farTop = -viewportHeight * 1.5;
  const farBottom = viewportHeight * 2.5;

  parallaxItems.forEach((item) => {
    const rect = item.element.getBoundingClientRect();

    if (rect.bottom < farTop || rect.top > farBottom) return;

    const elementCenterY = rect.top + rect.height / 2;
    const elementCenterX = rect.left + rect.width / 2;

    const dy = elementCenterY - centerY;
    const dx = elementCenterX - centerX;

    const translateY = clamp(-dy * item.speed, -item.maxTranslate, item.maxTranslate);
    const translateX = clamp(-dx * item.speed, -item.maxTranslate, item.maxTranslate);

    let transform = '';
    if (item.axis === 'y') {
      transform = `translate3d(0, ${translateY.toFixed(2)}px, 0)`;
    } else if (item.axis === 'x') {
      transform = `translate3d(${translateX.toFixed(2)}px, 0, 0)`;
    } else {
      transform = `translate3d(${translateX.toFixed(2)}px, ${translateY.toFixed(2)}px, 0)`;
    }

    if (transform !== item.lastTransform) {
      item.element.style.transform = transform;
      item.lastTransform = transform;
    }
  });
}

function useParallaxRef(options: ParallaxOptions = {}) {
  const {
    speed = 0.06,
    axis = 'y',
    maxTranslate = 40,
    rootMargin = '20% 0px 20% 0px',
  } = options;
  const elRef = useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    const el = elRef.current as HTMLElement | null;
    if (!el) return;

    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => {
      prefersReducedMotion = mql.matches;
      requestParallaxTick();
    };
    mql.addEventListener?.('change', onChange);

    ensureParallaxIO(rootMargin);
    startParallax();

    const item: ParallaxItem = {
      element: el,
      speed,
      axis,
      maxTranslate,
      inView: false,
      lastTransform: '',
    };

    try {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 0;
      const vm = Math.max(0, Math.min(0.5, parseFloat(rootMargin.split(' ')[0]) / 100 || 0.2));
      const extendedTop = -vh * vm;
      const extendedBottom = vh * (1 + vm);
      item.inView = rect.bottom >= extendedTop && rect.top <= extendedBottom;
    } catch {
      item.inView = true;
    }

    parallaxItems.add(item);
    parallaxIo?.observe(el);

    el.style.willChange = 'transform';
    el.style.backfaceVisibility = 'hidden';
    el.style.transformStyle = 'preserve-3d';

    updateParallax();

    return () => {
      mql.removeEventListener?.('change', onChange);
      parallaxIo?.unobserve(el);
      parallaxItems.delete(item);
      el.style.willChange = '';
      el.style.backfaceVisibility = '';
      el.style.transformStyle = '';
      el.style.transform = '';

      if (parallaxItems.size === 0) {
        stopParallax();
      }
    };
  }, [axis, maxTranslate, rootMargin, speed]);

  return elRef;
}

export default function Support() {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [filteredFaqs, setFilteredFaqs] = React.useState(FAQ_DATA);
  const [openFaqItems, setOpenFaqItems] = React.useState<Set<string>>(new Set());

  // Scroll handler for anchor links
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // Account for sticky header
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  // Toggle FAQ accordion
  const toggleFaq = (id: string) => {
    const newOpen = new Set(openFaqItems);
    if (newOpen.has(id)) {
      newOpen.delete(id);
    } else {
      newOpen.add(id);
      fireEvent('support_faq_expand', { faq_id: id });
    }
    setOpenFaqItems(newOpen);
  };

  // Subtle randomized, layered gradient background (same as Home)
  const backgroundStyle = useMemo<React.CSSProperties>(() => {
    const rand = (min: number, max: number) => Math.random() * (max - min) + min;

    const hue = rand(205, 220);
    const saturation = rand(65, 78);
    const lightNearWhite = rand(96, 98);
    const lightBlue = rand(88, 92);
    const alphaSoft1 = rand(0.06, 0.1);
    const alphaSoft2 = rand(0.04, 0.08);

    const centerX = rand(30, 70);
    const centerY = rand(15, 45);

    const verticalAngle = 180;
    const horizontalAngle = 90;

    const color1 = `hsla(${hue}, ${saturation}%, ${lightNearWhite}%, 1)`;
    const color2 = `hsla(${hue}, ${saturation}%, ${lightBlue}%, 1)`;
    const tint1 = `hsla(${hue}, ${saturation}%, ${lightBlue}%, ${alphaSoft1})`;
    const tint2 = `hsla(${hue}, ${saturation}%, ${lightNearWhite}%, ${alphaSoft2})`;

    const backgroundImage = [
      `radial-gradient(ellipse at ${centerX}% ${centerY}%, ${tint1} 0%, transparent 60%)`,
      `linear-gradient(${verticalAngle}deg, #ffffff 0%, ${color2} 100%)`,
      `linear-gradient(${horizontalAngle}deg, ${tint2} 0%, transparent 50%, ${tint2} 100%)`,
    ].join(', ');

    return {
      backgroundImage,
      backgroundColor: '#ffffff',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover, cover, cover',
      backgroundAttachment: 'scroll',
    };
  }, []);

  // Parallax refs
  const heroTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 70 });
  const heroSubRef = useParallaxRef({ speed: -0.18, axis: 'y', maxTranslate: 64 });

  React.useEffect(() => {
    fireEvent('support_view', { route: 'index' });
  }, []);

  React.useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredFaqs(FAQ_DATA);
      return;
    }

    const query = searchQuery.toLowerCase();
    const results = FAQ_DATA.filter(
      (faq) =>
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query) ||
        faq.tags?.some((tag) => tag.toLowerCase().includes(query)),
    );

    setFilteredFaqs(results);
    fireEvent('support_search', { q_len: query.length, results: results.length });
  }, [searchQuery]);

  return (
    <>
      <Helmet>
        <title>Support & Help Center - Attain Domains</title>
        <meta
          name="description"
          content="Get help with domain purchases, leasing, escrow, and account questions. Searchable FAQs and direct contact support."
        />
        <link rel="canonical" href="https://attaindomains.com/support" />
      </Helmet>

      <div className="min-h-screen pt-32 pb-12" style={backgroundStyle}>
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 ref={heroTitleRef as any} className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg">
              Support & Help Center
            </h1>
            <p ref={heroSubRef as any} className="text-3xl md:text-4xl text-foreground/70 max-w-4xl mx-auto leading-relaxed font-normal mb-8">
              Answers for what happens after you buy or lease
            </p>
            {/* Hero CTAs */}
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <Button
                size="lg"
                onClick={() => scrollToSection('after-purchase')}
                className="bg-primary hover:bg-primary/90"
              >
                I just bought a domain
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => scrollToSection('lease-to-own')}
              >
                I'm leasing a domain
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="flex justify-center mt-16 mb-16">
            <div className="max-w-2xl w-full">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search for help..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-14 text-lg"
                />
              </div>
              {searchQuery && (
                <p className="text-sm text-muted-foreground mt-2 text-center">
                  Found {filteredFaqs.length} result{filteredFaqs.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>

          {/* After Purchase Section */}
          {!searchQuery && (
            <section id="after-purchase" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">After you buy a domain</h2>
              <Card className="p-8">
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-semibold flex items-center justify-center">
                      1
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Order confirmation</h3>
                      <p className="text-muted-foreground">
                        You'll receive a Stripe receipt and Attain Domains confirmation email with your order details.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-semibold flex items-center justify-center">
                      2
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">We prepare your transfer or DNS access</h3>
                      <p className="text-muted-foreground">
                        We'll prepare your EPP (authorization) code and registrar steps. You'll receive the EPP code within 24-48 hours via email.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-semibold flex items-center justify-center">
                      3
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">You connect your website and email</h3>
                      <p className="text-muted-foreground">
                        Once DNS is active, you can point your domain to your website builder, hosting service, or email provider. <Link to="#technical-setup" className="text-primary hover:underline" onClick={(e) => { e.preventDefault(); scrollToSection('technical-setup'); }}>Learn more about DNS setup</Link>.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-semibold flex items-center justify-center">
                      4
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Ownership transfer (if applicable)</h3>
                      <p className="text-muted-foreground">
                        For BIN purchases, registrar ownership transfers after payment clears. You'll enter the EPP code at your registrar to start the transfer process. Transfers are final once the EPP code is delivered and submitted.
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </section>
          )}

          {/* Lease-to-Own Section */}
          {!searchQuery && (
            <section id="lease-to-own" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Lease-to-Own (LTO) support</h2>
              <Card className="p-8 mb-6">
                <div className="space-y-4">
                  <p className="text-muted-foreground">
                    Lease-to-Own lets you use a domain immediately while paying monthly. You get DNS access during the lease, and full registrar ownership transfers after your final payment clears.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 mt-6">
                    <div>
                      <h3 className="font-semibold mb-2">Terms available</h3>
                      <p className="text-sm text-muted-foreground">12, 24, or 36 months</p>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Early payoff</h3>
                      <p className="text-sm text-muted-foreground">Allowed anytime—contact support or use your account dashboard</p>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">DNS access</h3>
                      <p className="text-sm text-muted-foreground">Available immediately after your first payment</p>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Ownership</h3>
                      <p className="text-sm text-muted-foreground">Registrar ownership transfers after final payment</p>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <strong>Missed payments:</strong> If a payment fails or you miss a month, we'll email you to update your card. If unpaid past the grace period, the license may suspend and the domain can be returned to inventory.
                    </p>
                  </div>
                </div>
              </Card>
              {/* LTO FAQs */}
              <div className="space-y-3">
                {FAQ_DATA.filter(faq => faq.category === 'Leasing (Use It Monthly)' || faq.category === 'Lease-to-Own').slice(0, 5).map((faq) => {
                  const isOpen = openFaqItems.has(faq.id);
                  const answer = replaceFAQVariables(faq.answer, {});
                  return (
                    <Card key={faq.id} className="overflow-hidden">
                      <button
                        className="w-full text-left p-4 hover:bg-muted/30 transition-colors flex items-center justify-between"
                        onClick={() => toggleFaq(faq.id)}
                        aria-expanded={isOpen}
                      >
                        <span className="font-semibold pr-4">{faq.question}</span>
                        {isOpen ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-4 text-muted-foreground">
                          <p className="leading-relaxed">{answer}</p>
                          {faq.ctas && faq.ctas.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-4">
                              {faq.ctas.map((cta, idx) => (
                                <Button key={idx} variant="outline" size="sm" asChild>
                                  <Link to={cta.to}>{cta.label}</Link>
                                </Button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </section>
          )}

          {/* Billing & Account Section */}
          {!searchQuery && (
            <section id="billing-account" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Billing, invoices & account access</h2>
              <Card className="p-8">
                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold mb-2">Payment processing</h3>
                    <p className="text-muted-foreground">
                      All payments are processed securely via Stripe, a trusted payment provider. Your payment information is encrypted and never stored on my servers.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Invoices and receipts</h3>
                    <p className="text-muted-foreground">
                      You'll receive email receipts from Stripe for all payments. You can also view your payment history and download invoices through your account dashboard or the Stripe Customer Portal (accessible via "Manage Billing" in your account).
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Update payment methods</h3>
                    <p className="text-muted-foreground">
                      To update your card details or manage subscriptions, use the "Manage Billing" button in your account dashboard. This opens the Stripe Customer Portal where you can securely update payment methods, view invoices, and manage your subscription.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">LTO early payoff</h3>
                    <p className="text-muted-foreground">
                      You can pay off your Lease-to-Own early at any time. Contact support to request an early payoff link, or use the "Buy Now" option in your account dashboard if available. Early payoff transfers full ownership immediately.
                    </p>
                  </div>
                </div>
              </Card>
            </section>
          )}

          {/* Technical Setup Section */}
          {!searchQuery && (
            <section id="technical-setup" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Connecting your domain (DNS & setup)</h2>
              <Card className="p-8">
                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold mb-2">What is DNS?</h3>
                    <p className="text-muted-foreground mb-4">
                      DNS (Domain Name System) translates your domain name into an IP address so browsers can find your website. When you configure DNS, you're telling the internet where to send visitors when they type your domain.
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                      <li><strong>Nameservers:</strong> Point your domain to a DNS provider (e.g., Cloudflare, Route 53)</li>
                      <li><strong>A records:</strong> Point your domain to a specific IP address</li>
                      <li><strong>CNAME records:</strong> Point your domain to another domain name (e.g., www.example.com → example.com)</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Common uses</h3>
                    <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                      <li><strong>Point to a website builder or host:</strong> Most hosting providers give you DNS settings to point your domain to their servers</li>
                      <li><strong>Use for email:</strong> Configure MX records to route email to your email provider (e.g., Google Workspace, Microsoft 365)</li>
                      <li><strong>Redirect to another domain:</strong> Set up a redirect so visitors to your domain are automatically sent to another URL</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <strong>Need help?</strong> Your hosting or email provider typically provides DNS configuration instructions. If you're unsure, contact support and we can guide you through the process.
                    </p>
                  </div>
                </div>
              </Card>
            </section>
          )}

          {/* FAQ Section - Full Accordion */}
          {!searchQuery && (
            <section id="faqs" className="mb-16 scroll-mt-24">
              <h2 className="text-3xl md:text-4xl font-bold mb-8">Frequently asked questions</h2>
              <div className="space-y-8">
                {FAQ_CATEGORIES.map((category) => {
                  const categoryFaqs = FAQ_DATA.filter(faq => faq.category === category);
                  if (categoryFaqs.length === 0) return null;

                  return (
                    <div key={category}>
                      <h3 className="text-xl font-semibold mb-4">{category}</h3>
                      <div className="space-y-3">
                        {categoryFaqs.map((faq) => {
                          const isOpen = openFaqItems.has(faq.id);
                          const answer = replaceFAQVariables(faq.answer, {});
                          return (
                            <Card key={faq.id} className="overflow-hidden">
                              <button
                                id={`faq-question-${faq.id}`}
                                className="w-full text-left p-4 hover:bg-muted/30 transition-colors flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-primary/20"
                                onClick={() => toggleFaq(faq.id)}
                                aria-expanded={isOpen}
                                aria-controls={`faq-answer-${faq.id}`}
                              >
                                <span className="font-semibold pr-4">{faq.question}</span>
                                {isOpen ? (
                                  <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                                )}
                              </button>
                              {isOpen && (
                                <div
                                  id={`faq-answer-${faq.id}`}
                                  className="px-4 pb-4 text-muted-foreground"
                                  role="region"
                                  aria-labelledby={`faq-question-${faq.id}`}
                                >
                                  <p className="leading-relaxed whitespace-pre-line">{answer}</p>
                                  {faq.ctas && faq.ctas.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-4">
                                      {faq.ctas.map((cta, idx) => (
                                        <Button key={idx} variant="outline" size="sm" asChild>
                                          <Link to={cta.to} onClick={() => fireEvent(cta.event, { faq_id: faq.id })}>
                                            {cta.label}
                                          </Link>
                                        </Button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* FAQ Search Results */}
          {searchQuery && filteredFaqs.length > 0 && (
            <div className="space-y-4 mb-12">
              <h2 className="text-2xl font-bold mb-6">Search results</h2>
              {filteredFaqs.map((faq) => {
                const isOpen = openFaqItems.has(faq.id);
                const answer = replaceFAQVariables(faq.answer, {});
                return (
                  <Card key={faq.id} className="overflow-hidden">
                    <button
                      className="w-full text-left p-6 hover:bg-muted/30 transition-colors flex items-center justify-between"
                      onClick={() => toggleFaq(faq.id)}
                      aria-expanded={isOpen}
                    >
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold mb-2">{faq.question}</h3>
                        <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded">
                          {faq.category}
                        </span>
                      </div>
                      {isOpen ? (
                        <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0 ml-4" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0 ml-4" />
                      )}
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-6 text-muted-foreground">
                        <p className="leading-relaxed whitespace-pre-line">{answer}</p>
                        {faq.ctas && faq.ctas.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-4">
                            {faq.ctas.map((cta, idx) => (
                              <Button key={idx} variant="outline" size="sm" asChild>
                                <Link to={cta.to}>{cta.label}</Link>
                              </Button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}

          {searchQuery && filteredFaqs.length === 0 && (
            <div className="text-center py-12">
              <p className="text-lg text-muted-foreground mb-4">
                No articles found for "{searchQuery}"
              </p>
              <Button onClick={() => setSearchQuery('')} variant="outline">
                Clear search
              </Button>
            </div>
          )}

          {/* Contact Card */}
          <Card className="p-8 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">Still need help?</h3>
                  <p className="text-muted-foreground">Our team responds within 1 business day</p>
                </div>
              </div>
              <Link to="/support/contact">
                <Button size="lg" className="whitespace-nowrap">
                  Contact Support
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
